import { createContext, useContext, type ReactNode } from 'react';
import { setLocationHeader } from '../lib/supabase';
import { AccessDenied } from '../components/AccessDenied';

interface TenantContextType {
  locationId: string;
  userName: string | null;
  userEmail: string | null;
}

const TenantContext = createContext<TenantContextType>({
  locationId: '',
  userName: null,
  userEmail: null,
});

/** Extract and sanitize a URL param — strip HTML/script tags, limit length */
function getUrlParam(key: string): string | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(key);
  if (!raw || !raw.trim()) return null;
  // Strip any HTML tags and limit to 200 chars to prevent abuse
  const sanitized = raw.trim().replace(/<[^>]*>/g, '').slice(0, 200);
  return sanitized || null;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  // Public routes that don't require auth
  const isPublicRoute = window.location.pathname.startsWith('/script/') ||
    window.location.pathname.startsWith('/webinar/');
  if (isPublicRoute) {
    return <>{children}</>;
  }

  const locationId = getUrlParam('location_id');
  const userName = getUrlParam('user_name');
  const userEmail = getUrlParam('user_email');

  // No location_id = no access
  if (!locationId) {
    return <AccessDenied />;
  }

  // Set the location header for Supabase RLS policies
  setLocationHeader(locationId);

  return (
    <TenantContext.Provider value={{ locationId, userName, userEmail }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
